import type { ContentfulImageType } from '@bedrock/core/contentful';
import { getUrlFromFile } from '@bedrock/core/contentful';
import { BROptimisedImage, type OptimisedImageProps } from '@bedrock/core/react/components/client';
import { cn } from '@bedrock/ui/utils';
import { LazyWrap } from 'ui/common/lazy-wrap';
import { imageVariants, rootVariants } from './variants';

export type MediaImageProps = Readonly<
  {
    image?: ContentfulImageType;
    classNames?: {
      root?: string;
      image?: string;
      overlay?: string;
    };
    asBackground?: boolean;
    withOverlay?: boolean;
    preload?: boolean;
    width?: number;
    height?: number;
  } & Pick<OptimisedImageProps, 'fetchPriority'>
>;

export function MediaImage({
  image,
  classNames,
  asBackground = false,
  withOverlay = false,
  preload,
  fetchPriority,
  width,
  height,
}: MediaImageProps) {
  const fallbackAlt = asBackground ? 'Background image' : 'Image';

  return (
    <LazyWrap className={cn(rootVariants({ asBackground }), classNames?.root)}>
      <>
        {image ? (
          <BROptimisedImage
            alt={image.altText || fallbackAlt}
            className={cn(imageVariants({ asBackground }), classNames?.image)}
            fetchPriority={fetchPriority}
            height={height}
            mobileSrc={getUrlFromFile(image.secondaryImage)}
            priority={preload}
            src={getUrlFromFile(image.image)}
            width={width}
          />
        ) : null}
        {withOverlay ? (
          <div
            className={cn(
              'overlay bg-overlay-dark-60 absolute z-[-9] h-full w-full',
              classNames?.overlay,
            )}
          />
        ) : null}
      </>
    </LazyWrap>
  );
}
